import React, { useContext } from 'react';
import { LoadingOverlay, Skeleton } from '@mantine/core';
import type { FullContest } from '@betterpool/api-types/contests-service';
import useTranslation from 'next-translate/useTranslation';
import { useIsMutating } from '@tanstack/react-query';
import { useMediaQuery } from 'react-responsive';
import { useRouter } from 'next/router';

import { UserContext } from '~/components/providers/UserProvider';
import dt from '~/testing';
import { ButtonLink, LinkButton } from '~/domains/design-system/Button';
import { Routes } from '~/domains/common/constants/routes';

import { useEnableEditContest } from '../ContestHeader/hooks/useEnableEditContest';

import useStyles from './EntryButton.styles';

type EditContestButtonProps = {
  className?: string;
  contest: FullContest;
  isHidden?: boolean;
};

function EditContestButton({ className, contest, isHidden }: EditContestButtonProps) {
  const { classes, theme } = useStyles();
  const router = useRouter();
  const { t } = useTranslation('contest');
  const { user } = useContext(UserContext);
  const isTablet = useMediaQuery({ maxWidth: theme.breakpoints.sm });
  const { isEditContestEnabled, isEditContestReady } = useEnableEditContest();

  const isLoading = Boolean(useIsMutating(['createOrJoinEntry']));

  if (!isEditContestEnabled || !isEditContestReady || isHidden) return null;

  if (isTablet) {
    return (
      <Skeleton
        visible={user && isLoading}
        h={40}
        w={`${'max-content'}`}
        className={classes.headerEdit}
      >
        <LoadingOverlay visible={user && isLoading} loaderProps={{ size: 'sm' }} />
        <LinkButton
          variant="text"
          size="medium"
          data-test-id={dt.contestDetail.page.header.actions.edit}
          onClick={() => router.push(Routes.editAContest(contest.id))}
        >
          {t('detail.header.actions.edit')}
        </LinkButton>
      </Skeleton>
    );
  }

  return (
    <Skeleton visible={user && isLoading} h={40} className={className}>
      <LoadingOverlay visible={user && isLoading} loaderProps={{ size: 'sm' }} />
      <ButtonLink
        variant="secondary-stroke"
        size="medium"
        data-test-id={dt.contestDetail.page.header.actions.edit}
        href={Routes.editAContest(contest.id)}
      >
        {t('detail.header.actions.edit')}
      </ButtonLink>
    </Skeleton>
  );
}

export default EditContestButton;
